import React, { useState } from 'react';
import { Button, Form, Icon } from 'semantic-ui-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, copy, showError } from '../helpers';
import AuthLayout from './AuthLayout';

const PasswordResetConfirm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [copyState, setCopyState] = useState('idle');
  const validLink = Boolean(email && token);

  const handleSubmit = async () => {
    if (!validLink || loading || newPassword) return;
    setLoading(true);
    try {
      const res = await API.post('/api/user/reset', { email, token });
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        setNewPassword(data);
      } else {
        showError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!newPassword) return;
    const copied = await copy(newPassword);
    setCopyState(copied ? 'success' : 'error');
  };

  return (
    <AuthLayout
      variant='reset'
      title={t('auth.reset.confirm.title')}
      subtitle={t('auth.reset.confirm.subtitle')}
    >
      {!validLink ? (
        <div className='auth-result auth-result-error' role='alert'>
          <span className='auth-result-icon'>
            <Icon name='unlinkify' />
          </span>
          <strong>{t('auth.reset.confirm.invalid_title')}</strong>
          <p>{t('auth.reset.confirm.invalid_description')}</p>
          <Link to='/reset' className='auth-primary-link'>
            {t('auth.reset.confirm.request_again')}
          </Link>
        </div>
      ) : newPassword ? (
        <div className='auth-result' role='status'>
          <span className='auth-result-icon'>
            <Icon name='check' />
          </span>
          <strong>{t('auth.reset.confirm.result_title')}</strong>
          <p>{t('auth.reset.confirm.result_description')}</p>
          <div className='auth-result-email'>
            <span>{t('auth.reset.email')}</span>
            <strong>{email}</strong>
          </div>
          <div className='auth-password-result'>
            <code>{newPassword}</code>
            <Button
              type='button'
              onClick={handleCopy}
              className='auth-copy-button'
            >
              <Icon name={copyState === 'success' ? 'check' : 'copy outline'} />
              {copyState === 'success'
                ? t('auth.reset.confirm.copied')
                : t('auth.reset.confirm.copy')}
            </Button>
          </div>
          {copyState === 'error' && (
            <p className='auth-copy-feedback is-error'>
              {t('auth.reset.confirm.copy_failed')}
            </p>
          )}
          <Button
            fluid
            type='button'
            disabled
            className='auth-primary-button auth-completed-button'
          >
            <Icon name='check circle outline' />
            {t('auth.reset.confirm.button_disabled')}
          </Button>
          <Link to='/login' className='auth-secondary-link'>
            {t('auth.reset.back_login')}
          </Link>
        </div>
      ) : (
        <Form size='large' onSubmit={handleSubmit}>
          <Form.Input
            fluid
            icon='mail outline'
            iconPosition='left'
            value={email}
            readOnly
            aria-label={t('auth.reset.email')}
          />
          <Button
            fluid
            type='submit'
            loading={loading}
            disabled={loading}
            className='auth-primary-button'
          >
            <span>
              {loading
                ? t('auth.reset.confirm.processing')
                : t('auth.reset.confirm.button')}
            </span>
            <i className='arrow right icon' aria-hidden='true' />
          </Button>
          <p className='auth-form-note'>{t('auth.reset.confirm.notice')}</p>
        </Form>
      )}
    </AuthLayout>
  );
};

export default PasswordResetConfirm;
